import React, { useEffect, useMemo, useState, useCallback } from "react";
import '../../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../../components/Common/TableContainer';
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { Link } from "react-router-dom";

// Import services
import {
  fetchPakets,
  createPakets,
  updatePakets,
  deletePakets
} from '../../../services/paketService';

import { getJenisSeleksi } from "../../../services/bidangService";
// Import listKategori from your kategoriService
import { listKategori } from "../../../services/Master/kategoriService"; // Assuming this is the path to your kategoriService

// Import components
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import DeleteModal from '../../../components/Common/DeleteModal';

const PaketList = () => {
  // Meta title
  document.title = "Paket";

  // State management
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedPaket, setSelectedPaket] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [jenisSeleksiOptions, setJenisSeleksiOptions] = useState([]);
  const [kategoriPaketOptions, setKategoriPaketOptions] = useState([]); // State for kategori paket options
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    kategori_paket: "",
    jenis_seleksi: ""
  });

  // Fetch data function
  const fetchData = useCallback(async (page, per_page, filterParams) => {
    try {
      setLoading(true);
      const { data: paketData, meta } = await fetchPakets(page, per_page, filterParams);

      let calculatedLastPage = 1;
      if (meta && meta.total && meta.per_page) {
        calculatedLastPage = Math.ceil(meta.total / meta.per_page);
      }

      setData(paketData || []);
      setPagination({
        page: meta?.page || 1,
        per_page: meta?.per_page || 10,
        total: meta?.total || 0,
        last_page: calculatedLastPage
      });
    } catch (error) {
      console.error("Error in fetchData:", error);
      toast.error("Gagal memuat data paket");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch jenis seleksi options
  const fetchJenisSeleksi = useCallback(async () => {
    try {
      const response = await getJenisSeleksi();
      if (response.success) {
        const normalizedJenisSeleksi = (response.data || []).map(jenis =>
          typeof jenis === 'string' ? jenis.trim().toUpperCase() : ''
        );
        setJenisSeleksiOptions(normalizedJenisSeleksi);
      }
    } catch (error) {
      console.error("Error fetching jenis seleksi:", error);
      toast.error("Gagal memuat daftar jenis seleksi");
      setJenisSeleksiOptions([]);
    }
  }, []);

  // New: Fetch Kategori Paket options using listKategori
  const fetchKategoriPaketOptions = useCallback(async () => {
    try {
      const response = await listKategori(); // Call your existing listKategori function
      if (response && response.data) {
        // CORRECTED LINE: Use 'item.kategori' to access the category name
        const normalizedKategori = (response.data || []).map(item => item.kategori);
        setKategoriPaketOptions(normalizedKategori);
      }
    } catch (error) {
      console.error("Error fetching kategori paket options:", error);
      toast.error("Gagal memuat daftar kategori paket");
      setKategoriPaketOptions([]);
    }
  }, []);

  // Initial data fetch, jenis seleksi options, and kategori paket options fetch
  useEffect(() => {
    fetchData(pagination.page, pagination.per_page, filters);
    fetchJenisSeleksi();
    fetchKategoriPaketOptions(); // Call the fetch function for categories
  }, [fetchData, fetchJenisSeleksi, fetchKategoriPaketOptions, pagination.page, pagination.per_page, filters]);

  // Modal handling
  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setSelectedPaket(null);
      setIsEdit(false);
      validation.resetForm();
      validation.setErrors({});
      validation.setTouched({});
    } else {
      setModal(true);
    }
  };

  const handlePaketClick = (paketData) => {
    setSelectedPaket(paketData);
    setIsEdit(true);
    toggleModal();
  };

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      nama_paket: selectedPaket?.nama_paket || "",
      jenis_seleksi: selectedPaket?.jenis_seleksi ? String(selectedPaket.jenis_seleksi).trim().toUpperCase() : "",
      kategori_paket: selectedPaket?.kategori_paket || "",
      deskripsi: selectedPaket?.deskripsi || "",
      gambar: null,
    },
    validationSchema: Yup.object({
      nama_paket: Yup.string().required("Nama Paket wajib diisi."),
      kategori_paket: Yup.string().required("Kategori Paket wajib dipilih."),
      jenis_seleksi: Yup.string().required("Jenis Seleksi wajib dipilih."),
      deskripsi: Yup.string().required("Deskripsi wajib diisi."),
      gambar: Yup.mixed()
        .nullable()
        .test("fileSize", "Ukuran gambar terlalu besar (maks 2MB).", (value) => {
          if (!value) return true;
          return value.size <= 2 * 1024 * 1024; // 2MB
        })
        .test("fileType", "Format gambar tidak didukung (hanya JPG, PNG, GIF).", (value) => {
          if (!value) return true;
          return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
        }),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const formData = new FormData();
        Object.keys(values).forEach(key => {
          if (key === 'gambar' && values.gambar) {
            formData.append('gambar', values.gambar);
          } else if (key !== 'gambar') {
            formData.append(key, values[key]);
          }
        });

        let response;
        if (isEdit) {
          response = await updatePakets(selectedPaket.id_paket, formData);
        } else {
          response = await createPakets(formData);
        }

        if (response.success) {
          toast.success(response.message || (isEdit ? "Paket berhasil diperbarui." : "Paket berhasil ditambahkan."));
          fetchData(pagination.page, pagination.per_page, filters);
          toggleModal();
        } else {
          if (response.code === 409) {
            toast.error(response.message || "Terjadi Kesalahan: Konflik data.");
          } else {
            toast.error(response.message || "Gagal menyimpan paket.");
          }
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        toast.error(error.response?.data?.message || "Terjadi kesalahan saat menyimpan.");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Delete handling
  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setSelectedPaket(null);
  };

  const handleDeleteClick = (paketData) => {
    setSelectedPaket(paketData);
    setDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (selectedPaket && selectedPaket.id_paket) {
        const response = await deletePakets(selectedPaket.id_paket);
        if (response.success) {
          toast.success("Paket berhasil dihapus.");
          fetchData(pagination.page, pagination.per_page, filters);
        } else {
          toast.error(response.message || "Gagal menghapus paket.");
        }
      }
    } catch (error) {
      console.error("Error deleting paket:", error);
      toast.error(error.response?.data?.message || "Gagal menghapus paket.");
    } finally {
      handleCloseDeleteModal();
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = {
      ...filters,
      [name]: value
    };
    setFilters(newFilters);
    fetchData(1, pagination.per_page, newFilters);
  };

  // Clear all filters
  const clearFilters = () => {
    const defaultFilters = {
      kategori_paket: "",
      jenis_seleksi: ""
    };
    setFilters(defaultFilters);
    fetchData(1, pagination.per_page, defaultFilters);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Nama Paket",
        accessorKey: "nama_paket",
        enableColumnFilter: false,
      },
      {
        header: 'Kategori Paket',
        accessorKey: "kategori_paket",
        enableColumnFilter: false,
      },
      {
        header: 'Jenis Seleksi',
        accessorKey: "jenis_seleksi",
        enableColumnFilter: false,
      },
      {
        header: 'Deskripsi',
        accessorKey: "deskripsi",
        enableColumnFilter: false,
        cell: (cell) => (
          <div style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '200px'
          }}>
            {cell.getValue()}
          </div>
        )
      },
      {
        header: 'Created At',
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cellProps) => {
          const date = new Date(cellProps.getValue());
          return date.toISOString().split('T')[0];
        }
      },
      {
        header: 'Action',
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="hstack gap-2">
            <Link
              to={`/add-sub-paket/${cellProps.row.original.id_paket}`}
              className="btn btn-sm btn-soft-primary"
              id={`addtooltip-${cellProps.row.original.id_paket}`}
            >
              <i className="mdi mdi-plus" />
              <UncontrolledTooltip
                placement="top"
                target={`addtooltip-${cellProps.row.original.id_paket}`}
              >
                Add Sub Paket
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="btn btn-sm btn-soft-info"
              onClick={() => handlePaketClick(cellProps.row.original)}
              id={`edittooltip-${cellProps.row.original.id_paket}`}
            >
              <i className="mdi mdi-pencil-outline" />
              <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id_paket}`} >
                Edit
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="btn btn-sm btn-soft-danger"
              onClick={() => handleDeleteClick(cellProps.row.original)}
              id={`deletetooltip-${cellProps.row.original.id_paket}`}
            >
              <i className="mdi mdi-delete-outline" />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id_paket}`}>
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        )
      },
    ],
    [handlePaketClick, handleDeleteClick]
  );

  // Custom Pagination Component
  const CustomPagination = () => {
    const { page, last_page, total, per_page } = pagination;

    if (total === 0) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i} active={i === page}>
            <PaginationLink onClick={() => fetchData(i, per_page, filters)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
      return pages;
    };

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * per_page, total)}</span> dari{' '}
          <span className="fw-semibold">{total}</span> data
        </div>

        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            <PaginationItem disabled={page === 1}>
              <PaginationLink
                previous
                onClick={() => page > 1 && fetchData(page - 1, per_page, filters)}
              >
                &lt;
              </PaginationLink>
            </PaginationItem>

            {getPageNumbers()}

            <PaginationItem disabled={page === last_page}>
              <PaginationLink
                next
                onClick={() => page < last_page && fetchData(page + 1, per_page, filters)}
              >
                &gt;
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Paket" breadcrumbItem="List Paket" />
          {
            loading ? <Spinners setLoading={setLoading} />
              :
              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      {/* Baris Judul dan Tombol Tambah */}
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="mb-0 card-title">Daftar Paket</h5>
                        <Button
                          color="primary" 
                          onClick={toggleModal}
                          
                          style={{ backgroundColor: '#626ed4', borderColor: '#626ed4' }} 
                        >
                          <i className="mdi mdi-plus me-1"></i> Tambah
                        </Button>
                      </div>

                      {/* Baris Filter */}
                      <div className="mb-4"> {/* Margin bawah untuk jarak ke tabel */}
                        <Row className="align-items-end g-3"> {/* Gunakan g-3 untuk gap antar kolom */}
                          {/* Filter Kategori Paket */}
                          <Col xs={12} sm={6} md={4} lg={3}> {/* Lebar kolom responsif */}
                            <div>
                              <Label htmlFor="kategoriPaketFilter" className="form-label">Kategori Paket</Label>
                              <Input
                                id="kategoriPaketFilter"
                                type="select"
                                name="kategori_paket"
                                value={filters.kategori_paket}
                                onChange={handleFilterChange}
                                className="form-select"
                              >
                                <option value="">Semua Kategori</option>
                                {kategoriPaketOptions.map((kategori, index) => (
                                  <option key={index} value={kategori}>
                                    {kategori}
                                  </option>
                                ))}
                              </Input>
                            </div>
                          </Col>

                          {/* Filter Jenis Seleksi */}
                          <Col xs={12} sm={6} md={4} lg={3}>
                            <div>
                              <Label htmlFor="jenisSeleksiFilter" className="form-label">Jenis Seleksi</Label>
                              <Input
                                id="jenisSeleksiFilter"
                                type="select"
                                name="jenis_seleksi"
                                value={filters.jenis_seleksi}
                                onChange={handleFilterChange}
                                className="form-select"
                              >
                             <option value="">Semua Jenis Seleksi</option>
                              <option value="CPNS">CPNS</option>
                              <option value="PPPK">PPPK</option>
                              <option value="KEDINASAN">KEDINASAN</option>
                              <option value="TNI">TNI</option>
                              <option value="POLRI">POLRI</option>
                              <option value="SPPI">SPPI</option>
                              </Input>
                            </div>
                          </Col>

                          {/* Tombol Reset Filter */}
                          <Col xs={12} sm={6} md={2} lg={2}> {/* Kolom untuk tombol, responsif */}
                            <div className="d-grid"> {/* d-grid untuk membuat tombol penuh lebar kolom */}
                              <Label htmlFor="resetFilterBtn" className="form-label" style={{ visibility: 'hidden' }}>Reset</Label>
                              <Button
                                id="resetFilterBtn"
                                color="danger"
                                outline
                                onClick={clearFilters}
                              >
                                <i className="mdi mdi-refresh me-1"></i> Reset
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={false}
                        isPagination={false}
                        customPageSize={pagination.per_page}
                        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                      />
                      {pagination.total > 0 && <CustomPagination />}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
          }

          {/* Modal for Add/Edit */}
          <Modal isOpen={modal} toggle={toggleModal} size="lg" centered>
            <ModalHeader toggle={toggleModal} tag="h4">
              {!!isEdit ? "Edit Paket" : "Tambah Paket Baru"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md={12}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="nama_paket">Nama Paket</Label>
                      <Input
                        name="nama_paket"
                        type="text"
                        id="nama_paket"
                        placeholder="Masukkan nama paket"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.nama_paket || ""}
                        invalid={validation.touched.nama_paket && !!validation.errors.nama_paket}
                        className="form-control"
                      />
                      <FormFeedback>
                        {validation.errors.nama_paket}
                      </FormFeedback>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="kategori_paket">Kategori Paket</Label>
                      <Input
                        type="select"
                        name="kategori_paket"
                        id="kategori_paket"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.kategori_paket || ""}
                        invalid={validation.touched.kategori_paket && !!validation.errors.kategori_paket}
                        className="form-select"
                      >
                        <option value="">Pilih Kategori...</option>
                        {kategoriPaketOptions.map((kategori, index) => (
                          <option key={index} value={kategori}>
                            {kategori}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>
                        {validation.errors.kategori_paket}
                      </FormFeedback>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="jenis_seleksi_modal">Jenis Seleksi</Label>
                      <Input
                        type="select"
                        name="jenis_seleksi"
                        id="jenis_seleksi_modal"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.jenis_seleksi || ""}
                        invalid={validation.touched.jenis_seleksi && !!validation.errors.jenis_seleksi}
                        className="form-select"
                      >
                         <option value="">Semua Jenis Seleksi</option>
                              <option value="CPNS">CPNS</option>
                              <option value="PPPK">PPPK</option>
                              <option value="KEDINASAN">KEDINASAN</option>
                              <option value="TNI">TNI</option>
                              <option value="POLRI">POLRI</option>
                              <option value="SPPI">SPPI</option>
                      </Input>
                      <FormFeedback>
                        {validation.errors.jenis_seleksi}
                      </FormFeedback>
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="deskripsi">Deskripsi Paket</Label>
                      <Input
                        name="deskripsi"
                        type="textarea"
                        id="deskripsi"
                        rows="4"
                        placeholder="Masukkan deskripsi paket"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.deskripsi || ""}
                        invalid={validation.touched.deskripsi && !!validation.errors.deskripsi}
                        className="form-control"
                      />
                      <FormFeedback>
                        {validation.errors.deskripsi}
                      </FormFeedback>
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="gambar">Gambar Paket</Label>
                      <Input
                        name="gambar"
                        type="file"
                        id="gambar"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.currentTarget.files[0];
                          validation.setFieldValue("gambar", file);
                          validation.setFieldError("gambar", undefined);
                          validation.setFieldTouched("gambar", true, false);
                        }}
                        onBlur={validation.handleBlur}
                        invalid={validation.touched.gambar && !!validation.errors.gambar}
                        className="form-control"
                      />
                      <FormFeedback>
                        {validation.errors.gambar}
                      </FormFeedback>
                      {isEdit && selectedPaket?.gambar && !validation.values.gambar && (
                        <div className="mt-2">
                          <small className="text-muted">Gambar saat ini:</small>
                          <div className="mt-1">
                            <img
                              src={`${process.env.REACT_APP_API_URL}/storage/${selectedPaket.gambar}`}
                              alt="Current Paket"
                              style={{ maxWidth: '150px', maxHeight: '150px' }}
                              className="img-thumbnail"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button color="light" onClick={toggleModal} disabled={isSubmitting}>
                    Batal
                  </Button>
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border me-2" role="status" aria-hidden="true"></span>
                        Loading...
                      </>
                    ) : (
                      isEdit ? "Simpan Perubahan" : "Tambah Paket"
                    )}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </React.Fragment>
  );
}

export default PaketList;